import React, { useState } from 'react';
import { isEmpty, round, split } from 'lodash';
import { abs, evaluate, exp, i, inv, re } from 'mathjs';
import { INVENTORY_TYPES } from 'src/constants';
import { toDecimal4 } from 'src/utils/toDecimal';
import { v4 as uuid } from 'uuid';
import { qtyPCToCS } from 'src/helpers';

export const getParticularsAmount = particulars => {
  if (particulars.length === 0) return 0;

  return particulars
    .map(({ Amt }) => Amt.value)
    .reduce((prev, curr, indx) => Number(prev) + Number(curr));
};

export const validateForm = ({ data, excludes = [], allowZeroFlds = [] }) => {
  let newData = { ...data };
  let hasError = false;

  Object.entries(newData).forEach(([key, { require, value, ...rest }]) => {
    const isRequired = require || rest?.isRequired || rest?.required;

    if (
      !isRequired ||
      excludes.includes(key) ||
      (allowZeroFlds.includes(key) && value === 0)
    ) {
      return;
    }

    if (rest?.type === 'json' && rest?.ui_component === 'sub_acc') {
      const id = value?.id ?? '';
      const empty = id === '' || id === 0;

      if (empty) {
        newData[key].error = true;
        newData[key].errorMessage = 'This field is required';
        hasError = true;
        return;
      }
    }

    const empty = value === 0 || value === '' || typeof value === 'undefined';
    if (empty) {
      newData[key].error = true;
      newData[key].errorMessage = 'This field is required';
      hasError = true;
      return;
    }
  });

  return { hasError, newData };
};

export const solveExpression = ({ expression = '', variables = {} }) => {
  const exp = [...expression];

  if (exp[0] !== '=') return 'Invalid formula';

  exp.splice(0, 1); // remove '='

  try {
    return evaluate(exp.join(''), variables);
  } catch (error) {
    return 'Invalid formula';
  }
};

export const setSelfValueVariable = ({ value, formula = '' }) => {
  const selfVariables = ['cos'];
  let copyFormula = formula;

  for (let i = 0; i < selfVariables.length; i++) {
    const item = selfVariables[i];

    if (copyFormula.indexOf(item))
      copyFormula = copyFormula.replace(item, value);
  }

  return copyFormula;
};

export const recomputeJournalEntry = ({ journalEntries, variables }) => {
  let copyJournalEntries = [...journalEntries];

  let updatedJERows = [];

  if (copyJournalEntries.length === 0) return [];

  for (let i = 0; i < journalEntries.length; i++) {
    const item = journalEntries[i];

    // if no formula just get the bal
    if (item.Cr.formula === '' && item.Dr.formula === '') continue;

    const amountType = item.Cr.formula && item.Cr.formula !== '' ? 'Cr' : 'Dr';

    const expression = setSelfValueVariable({
      value: item[amountType].value,
      formula: item[amountType].formula
    });

    // If NaN value return 0
    const result = isNaN(solveExpression({ expression, variables }))
      ? 0
      : solveExpression({ expression, variables });

    if (result === 'Invalid formula') {
      copyJournalEntries[i][amountType].value = 0; // update the row
      copyJournalEntries[i][amountType].error = true; // update the row
      copyJournalEntries[i][amountType].errorMessage =
        'Invalid Formula : ' + item[amountType].formula; // update the row

      continue;
    }

    // if expression result is the same with stored amount just update the balance
    if (round(result, 4) === round(item[amountType].value, 4)) {
      copyJournalEntries[i][amountType].value = result; // update the row
      continue;
    }

    copyJournalEntries[i][amountType].value = result; // update the row

    // create a copy of updated row
    let updatedRow = { ...item };
    updatedRow[amountType].value = result;

    // push updated row
    updatedJERows.push({
      updatedRow,
      jeIndex: i
    });
  }

  return updatedJERows;
};

export const getKVSTableVariables = (value, kvs_key, settings) => {
  const obj = settings.columns.reduce((acc, { field, total }) => {
    if (total) {
      acc = {
        ...acc,
        [`jv_kvs_${kvs_key}_${field}`]: value.reduce(
          (t, p) => t + (p?.[field] ?? 0),
          0
        )
      };
    }
    return acc;
  }, {});

  const otherVars = value.reduce((acc, prev) => {
    if (Boolean(prev?.createVariable)) {
      acc = {
        ...acc,
        [`jv_kvs_${kvs_key}_var_${prev?.createVariable?.name}`]:
          prev?.[prev?.createVariable?.key] ?? 0
      };
    }
    return acc;
  }, {});

  return { ...obj, ...otherVars };
};

export const getKvsNumberValues = kvs => {
  let kvsNumberValues = {};

  Object.entries(kvs).forEach(([key, { type, value, ...rest }]) => {
    if (rest?.ui_component === 'select' && type !== 'int') return;

    if (rest?.ui_component === 'sc_pwd_discount') {
      kvsNumberValues = {
        ...kvsNumberValues,
        [`jv_kvs_${key}_amount`]: value?.amount ?? 0
      };
    }

    if (rest?.ui_component === 'table') {
      const kvsTableVariables = getKVSTableVariables(value, key, rest);
      kvsNumberValues = {
        ...kvsNumberValues,
        ...kvsTableVariables
      };
    }

    if (type === 'int') {
      kvsNumberValues = {
        ...kvsNumberValues,
        [`jv_kvs_${key}`]: isNaN(value) ? 0 : Number(value)
      };
    }
  });

  return kvsNumberValues;
};

const getValueFromSubAccount = (key, subAccountDetails) => {
  const splittedKey = split(key, '.');

  if (splittedKey.length === 1)
    return subAccountDetails?.[splittedKey[0]] ?? '';

  if (splittedKey[0] === 'kvs') {
    const kvs = subAccountDetails?.kvs ?? [];
    const keyIndex = kvs.findIndex(({ key }) => key === splittedKey[1]);

    return kvs?.[keyIndex]?.value ?? '';
  }

  return '';
};

export const saveSubAccountDetailsToKVS = ({
  settings = {},
  jvKVS = {},
  subAccountDetails = {}
}) => {
  let toUpdate = {};

  //! No settings
  if (isEmpty(settings)) return toUpdate;

  // * map_values => update kvs value where the source is sub account
  if (settings?.map_values) {
    Object.entries(settings?.map_values).forEach(([key, value]) => {
      //! key not found
      if (!Boolean(jvKVS[key])) return;

      toUpdate = {
        ...toUpdate,
        [key]: getValueFromSubAccount(value, subAccountDetails)
      };
    });
  }

  // * onchange_update_jv_kvs_values
  if (settings?.onchange_update_jv_kvs_values) {
    let updatedKvs = {};

    //* apply new updates
    Object.entries(jvKVS).forEach(([key, { value }]) => {
      updatedKvs = {
        ...updatedKvs,
        [key]: toUpdate?.[key] ?? value
      };
    });

    //* key => kvs key to update
    //* props.key => source key
    //* source_map

    Object.entries(settings?.onchange_update_jv_kvs_values).forEach(
      ([key, props]) => {
        //* if key to update not exist
        if (!updatedKvs.hasOwnProperty(key)) return;

        //* if source key not exist
        if (!props?.key) return;

        //* source map not exist
        if (!props?.source_map) return;

        // * create source
        let source = {};
        Object.entries(props.source_map).map(([key_source, value]) => {
          source = {
            ...source,
            [key_source]: updatedKvs?.[value] ?? ''
          };
        });

        //* get the key value
        const keyValue = updatedKvs?.[props.key] ?? '';

        toUpdate = {
          ...toUpdate,
          [key]: source?.[keyValue] ?? ''
        };
      }
    );
  }

  return toUpdate;
};

export const getInventoryVariables = ({
  inventory = [],
  invType = 0,
  amtType = 'OUT'
}) => {
  const {
    invGrossAmt,
    invPriceIN,
    invPriceOUT,
    invTotalMU,
    invTotalDiscOUT,
    invTotalDisc,
    invTotalDiscValue,
    invTotalGrossAmt,
    invTotalGrossOfVAT,
    invTotalNetOfVAT,

    invTotalNonVAT,
    invTotalVatable,
    invTotalExempt,
    invTotalZeroRated,

    invNewPrice,
    invNewVatIN,
    invNewPriceTotalNonVAT,
    invNewPriceTotalVatable,
    invNewPriceTotalExempt,
    invNewPriceTotalZeroRated,
    invRebate,

    invAmt,
    invVatIN,
    invVatOUT,
    invTotalItems,
    invTotalQtyIN,
    invTotalQtyOUT,
    invTotalAmtIN,
    invTotalAmtOUT
  } = inventory.reduce(
    (acc, prev) => {
      const qty = toDecimal4(+prev.qty.value),
        price = +prev.price.value,
        muAmt = toDecimal4(+prev.muAmt),
        discOutrightAmt = toDecimal4(+prev.discOutrightAmt),
        discAmt = toDecimal4(+prev.discAmt),
        discValue = toDecimal4(+prev.discValue),
        vatOUTAmt = toDecimal4(+prev.vatOUTAmt),
        vatINAmt = toDecimal4(+prev.vatINAmt),
        new_vatINAmt = toDecimal4(+prev.new_vatINAmt),
        priceINAmt = toDecimal4(+prev.priceIN) * qty,
        priceOUTAmt = toDecimal4(+prev.priceOUT) * qty,
        newPriceAmt = toDecimal4(+prev.new_price.value) * qty,
        grossAmt = toDecimal4(qty * price),
        taxablePrice = toDecimal4(+prev.taxablePrice),
        totalTaxableAmt = toDecimal4(+prev.taxableAmt || taxablePrice * qty),
        vatCode = prev.vatCode,
        qtyREC = prev.qtyREC,
        qtyACT = prev?.qtyACT?.value ?? 0,
        rebate = prev?.rebate ?? 0;
      let vatAmt = 0;

      if (prev.isDeleted) return acc;

      acc.invGrossAmt += grossAmt;
      acc.invTotalMU += muAmt * qty;
      acc.invTotalDiscOUT += discOutrightAmt;
      acc.invTotalDisc += discAmt;
      acc.invTotalDiscValue += discValue;
      acc.invTotalGrossAmt += grossAmt;
      acc.invAmt +=
        // remove times qty cousing inbalance total
        // grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
        grossAmt + muAmt * qty - discOutrightAmt - discAmt - discValue;

      acc.invTotalItems += qty;

      acc.invVatIN += vatINAmt;
      acc.invVatOUT += vatOUTAmt;

      acc.invPriceIN += priceINAmt;
      acc.invPriceOUT += priceOUTAmt;

      if (prev.qtyType === 'IN') {
        if (invType === INVENTORY_TYPES.STOCK_ADJUSTMENT_BY_VARIANCE) {
          acc.invTotalQtyIN += qtyACT - qtyREC;
        } else acc.invTotalQtyIN += qty;

        acc.invTotalAmtIN +=
          grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
      }

      if (prev.qtyType === 'OUT') {
        if (invType === INVENTORY_TYPES.STOCK_ADJUSTMENT_BY_VARIANCE) {
          acc.invTotalQtyOUT += qtyREC - qtyACT;
        } else acc.invTotalQtyOUT += qty;

        acc.invTotalAmtOUT +=
          // remove times qty cousing inbalance total
          // grossAmt + muAmt * qty - discOutrightAmt - discAmt * qty - discValue;
          grossAmt + muAmt * qty - discOutrightAmt - discAmt - discValue;
      }

      acc.invNewPrice += newPriceAmt;
      acc.invNewVatIN += new_vatINAmt;
      acc.invRebate += rebate;

      if (amtType === 'OUT') vatAmt = vatOUTAmt;

      if (amtType === 'IN') vatAmt = vatINAmt;

      acc.invTotalGrossOfVAT += totalTaxableAmt;
      acc.invTotalNetOfVAT += totalTaxableAmt - vatAmt;

      if (vatCode === 0) acc.invTotalNonVAT += totalTaxableAmt;
      else if (vatCode === 1) acc.invTotalVatable += totalTaxableAmt - vatAmt;
      else if (vatCode === 2) acc.invTotalExempt += totalTaxableAmt;
      else if (vatCode === 3) acc.invTotalZeroRated += totalTaxableAmt;

      if (vatCode === 0) acc.invNewPriceTotalNonVAT += newPriceAmt;
      else if (vatCode === 1)
        acc.invNewPriceTotalVatable += newPriceAmt - new_vatINAmt;
      else if (vatCode === 2) acc.invNewPriceTotalExempt += newPriceAmt;
      else if (vatCode === 3) acc.invNewPriceTotalZeroRated += newPriceAmt;

      return acc;
    },
    {
      invGrossAmt: 0,
      invPriceIN: 0,
      invPriceOUT: 0,
      invTotalMU: 0,
      invTotalDiscOUT: 0,
      invTotalDisc: 0,
      invTotalDiscValue: 0,
      invTotalGrossAmt: 0,
      invTotalGrossOfVAT: 0,
      invTotalNetOfVAT: 0,

      invTotalNonVAT: 0,
      invTotalVatable: 0,
      invTotalExempt: 0,
      invTotalZeroRated: 0,

      invNewPrice: 0,
      invNewVatIN: 0,
      invNewPriceTotalNonVAT: 0,
      invNewPriceTotalVatable: 0,
      invNewPriceTotalExempt: 0,
      invNewPriceTotalZeroRated: 0,
      invRebate: 0,

      invAmt: 0,
      invVatIN: 0,
      invVatOUT: 0,
      invTotalItems: 0,
      invTotalQtyIN: 0,
      invTotalQtyOUT: 0,
      invTotalAmtIN: 0,
      invTotalAmtOUT: 0
    }
  );

  return {
    invGrossAmt: invGrossAmt,
    invPriceIN: invPriceIN,
    invPriceOUT: invPriceOUT,
    invTotalMU: invTotalMU,
    invTotalDiscOUT: toDecimal4(invTotalDiscOUT),
    invTotalDisc: invTotalDisc,
    invTotalDiscValue: invTotalDiscValue,
    invTotalGrossAmt: invTotalGrossAmt,
    invTotalGrossOfVAT: invTotalGrossOfVAT,
    invTotalNetOfVAT: invTotalNetOfVAT,

    invTotalNonVAT: invTotalNonVAT,
    invTotalVatable: toDecimal4(invTotalVatable),
    invTotalExempt: invTotalExempt,
    invTotalZeroRated: invTotalZeroRated,

    invNewPrice: invNewPrice,
    invNewVatIN: invNewVatIN,
    invNewPriceTotalNonVAT: invNewPriceTotalNonVAT,
    invNewPriceTotalVatable: invNewPriceTotalVatable,
    invNewPriceTotalExempt: invNewPriceTotalExempt,
    invNewPriceTotalZeroRated: invNewPriceTotalZeroRated,
    invRebate: invRebate,

    invTotalAmtIN: toDecimal4(invTotalAmtIN),
    invTotalAmtOUT: toDecimal4(invTotalAmtOUT),

    invAmt: invAmt,
    invVatIN: invVatIN,
    invVatOUT: invVatOUT,
    invTotalItems,
    invTotalQtyIN,
    invTotalQtyOUT
  };
};

export const getJournalEntryVariables = ({
  journalEntries = [],
  cos_acc_id = 0,
  mi_account = 0,
  isAbsolute = true
}) => {
  const {
    bal,
    totalDr,
    totalCr,
    jeVatIN,
    jeVatOUT,
    jeWtIN,
    jeWtOUT,
    jeVatWtAmt,
    jeCOS,
    jeMI,
    jeQtyAndRateTotal,
    jeTaxGross,
    accountAmounts
  } = journalEntries.reduce(
    (acc, prev) => {
      const Dr = parseFloat(+prev.Dr.value);
      const Cr = parseFloat(+prev.Cr.value);
      const diff = Dr - Cr;

      const vatIN = parseFloat(+prev?.tax?.vatIN ?? 0);
      const vatOUT = parseFloat(+prev?.tax?.vatOUT ?? 0);
      const wtAmtIN = parseFloat(+prev?.tax?.wtAmtIN ?? 0);
      const wtAmtOUT = parseFloat(+prev?.tax?.wtAmtOUT ?? 0);
      const vatWTamt = parseFloat(+prev?.tax?.vatWTamt ?? 0);

      const reqQtyRate = +prev.reqQtyRate;
      const drQty = +prev.drQty;
      const crQty = +prev.crQty;
      const drRate = parseFloat(+prev.drRate);
      const crRate = parseFloat(+prev.crRate);
      const taxGross = parseFloat(+prev?.tax?.gross ?? 0);
      const ixAcc = +prev.ixAcc.value;

      acc.totalDr += Dr;
      acc.totalCr += Cr;
      acc.bal += diff;

      acc.jeVatIN += vatIN;
      acc.jeVatOUT += vatOUT;
      acc.jeWtIN += wtAmtIN;
      acc.jeWtOUT += wtAmtOUT;
      acc.jeVatWtAmt += vatWTamt;
      acc.jeTaxGross += taxGross;

      if (+prev.ixAcc.value === +cos_acc_id) acc.jeCOS += Dr;

      if (+prev.ixAcc.value === +mi_account) acc.jeMI += Cr;

      if (reqQtyRate === 1) acc.jeQtyAndRateTotal += drRate * drQty;

      if (reqQtyRate === 2) acc.jeQtyAndRateTotal += crRate * crQty;

      acc.accountAmounts[`acc_${ixAcc}_dr`] = Dr;
      acc.accountAmounts[`acc_${ixAcc}_cr`] = Cr;
      return acc;
    },
    {
      bal: 0,
      totalDr: 0,
      totalCr: 0,
      jeVatIN: 0,
      jeVatOUT: 0,
      jeWtIN: 0,
      jeWtOUT: 0,
      jeVatWtAmt: 0,
      jeCOS: 0,
      jeMI: 0,
      jeQtyAndRateTotal: 0,
      jeTaxGross: 0,
      accountAmounts: {}
    }
  );

  return {
    bal: isAbsolute ? Math.abs(bal) : bal,
    totalDr: totalDr,
    totalCr: totalCr,
    jeVatIN: jeVatIN,
    jeVatOUT: jeVatOUT,
    jeWtIN: jeWtIN,
    jeWtOUT: jeWtOUT,
    jeVatWtAmt: jeVatWtAmt,
    jeCOS: jeCOS,
    jeMI: jeMI,
    jeQtyAndRateTotal: jeQtyAndRateTotal,
    jeTaxGross: jeTaxGross,
    ...accountAmounts
  };
};

export function fillSubAccounts(ref, subDetails, refKey = '') {
  if (refKey === '') return;

  for (let i = 1; i <= 5; i++) {
    if (!Boolean(ref[`${refKey}${i}`])) continue;

    for (let j = 0; j <= 5; j++) {
      let ixSubLinkType = 0,
        ixSubLink = 0,
        sSubLink = '';

      if (j === 0) {
        ixSubLinkType = subDetails?.['ixSubParentType'] ?? 0;
        ixSubLink = subDetails?.['ixSubParent'] ?? 0;
        sSubLink = subDetails?.['sSubParent'] ?? '';
      } else {
        ixSubLinkType = subDetails?.['ixSubLinkType' + j] ?? 0;
        ixSubLink = subDetails?.['ixSubLink' + j] ?? 0;
        sSubLink = subDetails?.['sSubLink' + j] ?? '';
      }

      if (
        ixSubLinkType !== 0 &&
        ixSubLinkType === ref[`${refKey}${i}`].ixSubType &&
        (ref[`${refKey}${i}`].sub_id === 0 ||
          ref[`${refKey}${i}`].sub_id === '') &&
        ref[`${refKey}${i}`].sub_id !== ixSubLink
      ) {
        ref[`${refKey}${i}`].sub_id = ixSubLink;
        ref[`${refKey}${i}`].value = ixSubLink;
        ref[`${refKey}${i}`].sub_title = sSubLink;
        ref[`${refKey}${i}`].error = false;
        ref[`${refKey}${i}`].errorMessage = '';
      }
    }
  }
}

export function dealsValidator(results, inventoryProducts) {
  // findMainFree: Get the Initialize Kvs settings on deals
  const findMainFree = deals => {
    const results = {};

    deals.forEach(deal => {
      const id = deal.ixSub?.ixSub || deal.sub_id; // numeric ID
      results[id] = {
        ixSub: id,
        main: deal.kvsData?.product || null,
        free: deal.kvsData?.free_products || null
      };
    });

    return results;
  };

  // creatNewDeal: It will destructure and format the deals
  const createNewDeal = (dealsProducts = {}) => {
    const result = {};

    Object.entries(dealsProducts).forEach(([dealId, dealData]) => {
      const { main, free } = dealData;

      // Transform free products
      const freeProducts = free?.map((item, index) => ({
        id: `free_${index}`,
        name: `Free Product ${item?.ixProd}`,
        code: `FP${item?.ixProd}`,
        category: 'Free Product',
        ixProd: item?.ixProd || '',
        qty: item?.qty || 0,
        invQty: item?.invQty || 0,
        price: 0,
        uuidRow: uuid(),
        uuidFreeParent: uuid(),
        isFromKVS: true
      }));
      // Transform main product

      const mainProduct = main?.map((item, index) => ({
        id: `main_${index}`,
        name: `Main Product ${item?.ixProd}`,
        code: `MP${item?.ixProd}`,
        category: 'Main Product',
        ixProd: item?.ixProd || '',
        qty: item?.qty || 0,
        invQty: item?.invQty || 0,
        price: 0,
        uuidRow: uuid(),
        uuidFreeParent: uuid(),
        isFromKVS: true
      }));

      // Add to result using the dealId as the key
      result[dealId] = {
        mainProduct,
        freeProducts
      };
    });

    return result;
  };

  // dealsMatch: Matching for inv and deal products
  const dealsMatch = (deals = {}, inventoryProducts = []) => {
    const success = {};
    const warning = {};
    const error = {};
    const leftInventory = [];

    let proceed = true;
    const mergeDuplicateProducts = (deals, inventoryProducts = []) => {
      console.log('deals', deals);

      const merged = {};
      const result = [];

      inventoryProducts.forEach(item => {
        console.log('item', item);
        const prodCd = item?.product?.ProdCd;
        const qtyVal = item?.qty?.value || 0;
        const priceVal = item?.price?.value || 0;

        // If item has price, keep it separate
        if (priceVal > 0) {
          result.push(item);
          return;
        }

        // Merge only items without price
        if (!merged[prodCd]) {
          merged[prodCd] = {
            ...item,
            qty: { ...item.qty, value: qtyVal }
          };
        } else {
          merged[prodCd].qty.value += qtyVal;
        }
      });

      return [...Object.values(merged), ...result];
    };

    const cleanedInventory = mergeDuplicateProducts(deals, inventoryProducts);

    // Track used inventory IDs
    const usedInventoryIDs = Object.values(deals).reduce((acc, dealData) => {
      const mainIxProdList = (dealData.mainProduct || []).map(mp => mp.ixProd);
      const freeIxProdList = (dealData.freeProducts || []).map(fp => fp.ixProd);

      // Find all matching inventory for main + free
      const mainInventory = cleanedInventory.filter(item =>
        mainIxProdList.includes(item?.product?.value)
      );

      const freeInventory = cleanedInventory.filter(item =>
        freeIxProdList.includes(item?.product?.value)
      );

      // Add IDs to accumulator array (avoid duplicates)
      mainInventory.forEach(item => {
        if (!acc.includes(item?.product?.value)) acc.push(item?.product?.value);
      });

      freeInventory.forEach(item => {
        if (!acc.includes(item?.product?.value)) acc.push(item?.product?.value);
      });

      return acc;
    }, []);

    const invProds = cleanedInventory.slice(0, usedInventoryIDs.length);

    Object.entries(deals).forEach(([dealID, dealData]) => {
      const { mainProduct = [], freeProducts = [] } = dealData;

      const mainIxProdList = mainProduct.map(mp => mp.ixProd);
      const freeIxProdList = freeProducts.map(fp => fp.ixProd);

      // Find all matching inventory for main + free
      const mainInventory = invProds.filter(item =>
        mainIxProdList.includes(item?.product?.value)
      );

      const freeInventory = invProds.filter(item =>
        freeIxProdList.includes(item?.product?.value)
      );

      // Missing logic
      const missingMainIDs = mainIxProdList.filter(
        id => !mainInventory.some(i => i?.product?.value === id)
      );

      const missingFreeIDs = freeIxProdList.filter(
        id => !freeInventory.some(i => i?.product?.value === id)
      );

      const isComplete =
        missingMainIDs.length === 0 && missingFreeIDs.length === 0;

      // SUCCESS RESULT
      if (isComplete) {
        success[dealID] = {
          idMain: mainInventory.map(i => i?.product?.value),
          idFree: freeInventory.map(i => i?.product?.value),
          data: {
            mainInventory: mainInventory.map(inv => ({
              ...inv,
              deal: mainProduct.find(mp => mp.ixProd === inv?.product?.value)
            })),
            freeInventory: freeInventory.map(inv => ({
              ...inv,
              deal: freeProducts.find(fp => fp.ixProd === inv?.product?.value)
            }))
          }
        };
      } else {
        // WARNING RESULT
        warning[dealID] = {
          idMain: mainInventory.map(i => i?.product?.value),
          idFree: freeInventory.map(i => i?.product?.value),
          missingMainIDs,
          missingFreeIDs,
          data: {
            mainInventory: mainInventory.map(inv => ({
              ...inv,
              deal: mainProduct.find(mp => mp.ixProd === inv?.product?.value)
            })),
            freeInventory: freeInventory.map(inv => ({
              ...inv,
              deal: freeProducts.find(fp => fp.ixProd === inv?.product?.value)
            }))
          }
        };
      }

      // ERROR RESULT
      if (missingMainIDs.length > 0 || missingFreeIDs.length > 0) {
        error[dealID] = {
          missingMainIDs,
          missingFreeIDs
        };
      }
    });

    // Left inventory = not used items
    cleanedInventory.forEach(item => {
      if (!usedInventoryIDs.includes(item?.product?.value)) {
        leftInventory.push(item);
      }
    });

    // If any errors, proceed = false
    Object.values(error).forEach(err => {
      if (err.missingMainIDs.length || err.missingFreeIDs.length) {
        proceed = false;
      }
    });

    return {
      success,
      warning,
      error,
      leftInventory,
      proceed,
      cleanedInventory
    };
  };

  function getMostFrequentLowest(arr = []) {
    if (!Array.isArray(arr) || arr.length === 0) return 0;

    const freq = {};

    arr.forEach(num => {
      if (!isNaN(num)) {
        freq[num] = (freq[num] || 0) + 1;
      }
    });

    let result = null;
    let maxCount = 0;

    Object.entries(freq).forEach(([num, count]) => {
      const n = Number(num);

      if (count > maxCount || (count === maxCount && n < result)) {
        maxCount = count;
        result = n;
      }
    });

    return result;
  }

  const expectedValidation = dealObj => {
    const { base, input } = dealObj;
    const expectedMain = [];
    const expectedFree = [];

    let mainMultipliers = [];
    let freeMultipliers = [];

    // COLLECT MAIN MULTIPLIERS
    base.main.forEach(baseItem => {
      const mainInput = input.main.find(
        inputItem => inputItem.ProdCd === baseItem.ProdCd
      );

      const inputMain = Math.floor((mainInput?.qty ?? 0) / baseItem?.qty);
      mainMultipliers.push(inputMain);
    });

    // COLLECT FREE MULTIPLIERS
    base.free.forEach(baseItem => {
      const freeInput = input.free.find(
        inputItem => inputItem.ProdCd === baseItem.ProdCd
      );
      const inputFree = Math.floor((freeInput?.qty ?? 0) / baseItem?.qty);
      freeMultipliers.push(inputFree);
    });

    // MAIN PRODUCTS
    base.main.forEach(baseItem => {
      const main = getMostFrequentLowest(freeMultipliers) * baseItem.qty;
      const expected = qtyPCToCS({
        libQtyCS: baseItem?.libQtyCS ?? 0,
        libQtySC: baseItem?.libQtySC ?? 0,
        qty: main,
        keyProp: 'main'
      });

      const mainInput = input.main.find(
        inputItem => inputItem.ProdCd === baseItem.ProdCd
      );
      const inputMain = Math.floor((mainInput?.qty ?? 0) / baseItem?.qty);
      const frequent = getMostFrequentLowest(freeMultipliers);
      const regular = Math.max(0, (inputMain - frequent) * baseItem.qty);

      expectedMain.push({
        ProdCd: baseItem.ProdCd,
        input: mainInput,
        regular,
        item: baseItem,
        multiplier: frequent,
        exp: { ...expected, qty: main }
      });
    });

    // FREE PRODUCTS
    base.free.forEach(baseItem => {
      const free = getMostFrequentLowest(mainMultipliers) * baseItem.qty;
      const expected = qtyPCToCS({
        libQtyCS: baseItem?.libQtyCS ?? 0,
        libQtySC: baseItem?.libQtySC ?? 0,
        qty: free,
        keyProp: 'free'
      });

      const freeInput = input.free.find(
        inputItem => inputItem.ProdCd === baseItem.ProdCd
      );
      const inputFree = Math.floor((freeInput?.qty ?? 0) / baseItem?.qty);
      const frequent = getMostFrequentLowest(mainMultipliers);
      const regular = Math.max(0, (inputFree - frequent) * baseItem.qty);

      expectedFree.push({
        ProdCd: baseItem.ProdCd,
        regular,
        input: freeInput,
        item: baseItem,
        multiplier: frequent,
        exp: { ...expected, qty: free }
      });
    });

    return {
      expectedMain,
      expectedFree
    };
  };

  // qtyValidation: Comparing if the success data deal and inv prod qty is correct or invalid
  const qtyValidation = data => {
    const result = {}; // final result per deal
    const qtyValidate = [];

    const { success } = data;

    // Build expected vs input for each deal
    Object.entries(success).forEach(([dealID, item]) => {
      const dealObj = {
        dealID,
        base: { main: [], free: [] },
        input: { main: [], free: [] }
      };

      // --- MAIN INVENTORY ---
      const mainInventoryList = item?.data?.mainInventory || [];
      mainInventoryList.forEach(mainInv => {
        const baseMain = qtyPCToCS({
          libQtyCS: mainInv?.base_qtyCS ?? 0,
          libQtySC: mainInv?.base_qtySC ?? 0,
          qty: mainInv?.deal?.invQty ?? 0,
          keyProp: 'baseMain'
        });

        const inputMain = qtyPCToCS({
          libQtyCS: mainInv?.product?.qtyCS ?? 0,
          libQtySC: mainInv?.product?.qtySC ?? 0,
          qty: mainInv?.qty?.value ?? 0,
          keyProp: 'inputMain'
        });

        dealObj.base.main.push({
          ProdCd: mainInv?.product?.ProdCd,
          libQtyCS: mainInv?.base_qtyCS ?? 0,
          libQtySC: mainInv?.base_qtySC ?? 0,
          qty: mainInv?.deal?.invQty ?? 0,
          item: mainInv?.product,
          details: mainInv,
          ...baseMain
        });

        dealObj.input.main.push({
          ProdCd: mainInv?.product?.ProdCd,
          qty: mainInv?.qty?.value ?? 0,
          ...inputMain
        });
      });

      // --- FREE INVENTORY ---
      const freeInventoryList = item?.data?.freeInventory || {};
      Object.values(freeInventoryList).forEach(freeInv => {
        const baseFree = qtyPCToCS({
          libQtyCS: freeInv?.product?.qtyCS ?? 0,
          libQtySC: freeInv?.product?.qtySC ?? 0,
          qty: freeInv?.deal?.invQty ?? 0,
          keyProp: 'baseFree'
        });

        const inputFree = qtyPCToCS({
          libQtyCS: freeInv?.product?.qtyCS ?? 0,
          libQtySC: freeInv?.product?.qtySC ?? 0,
          qty: freeInv?.qty?.value ?? 0,
          keyProp: 'inputFree'
        });

        dealObj.base.free.push({
          ProdCd: freeInv?.product?.ProdCd,
          libQtyCS: freeInv?.product?.qtyCS ?? 0,
          libQtySC: freeInv?.product?.qtySC ?? 0,
          item: freeInv?.product,
          qty: freeInv?.deal?.invQty ?? 0,
          details: freeInv,
          ...baseFree
        });

        dealObj.input.free.push({
          ProdCd: freeInv?.product?.ProdCd,
          qty: freeInv?.qty?.value ?? 0,
          ...inputFree
        });
      });

      const expected = expectedValidation(dealObj);
      qtyValidate.push({ dealID, expected });
    });

    // --- VALIDATE EACH DEAL ---
    qtyValidate.forEach(({ dealID, expected }) => {
      const dealResult = {
        main: { success: {}, warning: {}, error: {} },
        free: { success: {}, warning: {}, error: {} },
        proceed: { allow: true, msg: '' }
      };

      // --- MAIN ---
      expected.expectedMain.forEach(
        ({ ProdCd, input, exp, item, regular, multiplier, details }) => {
          if (
            input.inputMain_CS !== exp.main_CS ||
            input.inputMain_SC !== exp.main_SC ||
            input.inputMain_PC !== exp.main_PC ||
            input.qty !== exp.qty
          ) {
            const type =
              input.qty > exp.qty
                ? 'error'
                : input.qty < exp.qty
                ? 'warning'
                : 'success';

            dealResult.main.warning[ProdCd] = {
              ProdCd,
              qty: exp.qty,
              input,
              exp,
              item,
              regular,
              multiplier,
              details,
              msg: `Expected (CS:${exp.main_CS} | SC:${exp.main_SC} | PC:${exp.main_PC})`
            };
          } else {
            dealResult.main.success[ProdCd] = {
              ProdCd,
              qty: exp.qty,
              input,
              exp,
              regular,
              multiplier,
              item,
              details,
              msg: 'MAIN_QTY_MATCHED'
            };
          }
        }
      );

      // --- FREE ---
      expected.expectedFree.forEach(
        ({ ProdCd, input, exp, item, regular, multiplier, details }) => {
          if (
            input.inputFree_CS !== exp.free_CS ||
            input.inputFree_SC !== exp.free_SC ||
            input.inputFree_PC !== exp.free_PC ||
            input.qty !== exp.qty
          ) {
            const type =
              input.qty > exp.qty
                ? 'error'
                : input.qty < exp.qty
                ? 'warning'
                : 'success';

            dealResult.free.warning[ProdCd] = {
              ProdCd,
              qty: exp.qty,
              input,
              exp,
              item,
              multiplier,
              regular,
              details,
              msg: `Expected (CS:${exp.free_CS} | SC:${exp.free_SC} | PC:${exp.free_PC})`
            };
          } else if (item?.details?.price?.value > 0) {
            dealResult.free.error[ProdCd] = {
              ProdCd,
              qty: exp.qty,
              input,
              exp,
              item,
              multiplier,
              regular,
              details,
              msg: 'FREE_PRICE_NOT_ZERO'
            };
          } else {
            dealResult.free.success[ProdCd] = {
              ProdCd,
              qty: exp.qty,
              input,
              exp,
              item,
              multiplier,
              regular,
              details,
              msg: 'FREE_QTY_MATCHED'
            };
          }
        }
      );

      // --- PROCEED ---
      const hasError =
        // Object.keys(dealResult.main.error).length > 0 ||
        Object.keys(dealResult.free.error).length > 0;

      const hasWarning =
        // Object.keys(dealResult.main.warning).length > 0 ||
        Object.keys(dealResult.free.warning).length > 0;

      if (hasError) {
        dealResult.proceed.allow = false;
        dealResult.proceed.msg =
          'Quantity error or free product price must be zero.';
      } else if (hasWarning) {
        dealResult.proceed.allow = false;
        dealResult.proceed.msg =
          'Some quantities are lower than deal requirements.';
      }

      result[dealID] = dealResult;
    });

    return result;
  };

  const dealsProductsKvs = findMainFree(results);

  const kvsDeal = createNewDeal(dealsProductsKvs);
  const dealsMatched = dealsMatch(kvsDeal, inventoryProducts);
  const qtyResult = qtyValidation(dealsMatched);

  const allowSave = Object.values(qtyResult).every(
    item => item.proceed?.allow === true
  );

  const validation = {
    dealsProductsKvs,
    kvsDeal,
    matched: dealsMatched,
    resultQty: qtyResult,
    allowSave
  };

  return {
    dealsProductsKvs,
    kvsDeal,
    dealsMatched,
    qtyResult,
    validation
  };
}

export async function invAuto({
  arrayData: ixProdList,
  setIsSaving,
  uniqueData,
  transactionsData,
  alerts,
  meta,
  request,
  products,
  hasDuplicateSN,
  hasDuplicateSN2,
  INVENTORY_TYPES,
  inv,
  frontEnd,
  ALL_PRICE_TYPES,
  IN_INV,
  OUT_INV,
  QTY_INPUT_UI_MODE,
  CalculateDiscountsAndVAT,
  vat,
  notif,
  availableIndexes
}) {
  const { message } = alerts;
  const {
    PURCHASES,

    PURCHASE_ORDER,
    SALES_ORDER,
    WAVG_PURCHASES
  } = INVENTORY_TYPES;
  const {
    inventoryProducts,
    setInventoryProducts,

    priceType,
    vatType
  } = transactionsData;
  const {
    transactionMeta,

    allowInventoryVAT: vatAllow,

    qtyInputUIMode
  } = meta;
  const invType = transactionMeta?._rm?.ixInvType;
  const INvisible = IN_INV.includes(invType);
  const OUTvisible = OUT_INV.includes(invType);
  const discountsAndVAT = CalculateDiscountsAndVAT();
  const taxVATRate =
    transactionMeta?._rm?.webEntry?.trans_inv?.vat?.rate ?? vat?.rate ?? 0.12;
  const allow_adding_duplicate_products =
    transactionMeta?._rm?.webEntry?.trans_inv
      ?.allow_adding_duplicate_products ?? true;

  try {
    setIsSaving(true);

    let indexes = [...availableIndexes];

    for (const product of ixProdList) {
      const prodId = product.ixProd;
      const prodDetails = products.find(item => item.ixProd === prodId);

      if (!prodDetails) {
        notif.error(`Product not found ${product?.ixProd}`);
        continue;
      }

      const { ixProd, ...rest } = prodDetails;
      // INDEX: Finding empty index to avoid overiding existing inputed product
      // Find index of the current product in the list
      const idIndex = ixProdList.findIndex(item => item.ixProd === prodId);
      const usingIndex = indexes || [];
      const isCurrentIndexAvailable = availableIndexes.includes(idIndex);

      let finalIndex = null;

      // Prefer matching index from ixProdList if still available
      if (indexes.includes(idIndex)) {
        finalIndex = idIndex;
      } else if (indexes.length > 0) {
        finalIndex = indexes.shift(); // take first available
      } else {
        notif.error('Insufficient rows, remaining products must match on rows');
        continue; // cannot insert, skip
      }

      indexes = indexes.filter(i => i !== finalIndex);

      const id = inventoryProducts[finalIndex]?.key;
      const index = inventoryProducts.findIndex(item => item.key === id);

      if (usingIndex.length === 0) {
        console.warn('No available index to insert product. Process stopped.');
        return; // stop further execution
      }

      if (!prodDetails) continue;
      const hasDuplicateProduct = ixProd => {
        return inventoryProducts.some(
          ({ product, key, isDeleted, reqSN, reqSN2 }) => {
            if ([SALES_ORDER, PURCHASE_ORDER].includes(invType)) {
              return !isDeleted && id !== key && +product.value === +ixProd;
            } else {
              return (
                !isDeleted &&
                id !== key &&
                +product.value === +ixProd &&
                !(inv?.BN?.required_categories ?? []).includes(product.ixCat) &&
                !(reqSN || reqSN2)
              );
            }
          }
        );
      };

      const confirmPrompt = async (msg = '') => {
        const text =
          typeof msg === 'object' ? JSON.stringify(msg, null, 2) : String(msg);

        const confirm = window.confirm(text);
        return confirm;
      };

      const getPrice = prodDetails => {
        const invType = transactionMeta?._rm?.ixInvType;

        if (!Boolean(transactionMeta?._rm?.webEntry?.priceType?.allow)) {
          // settings to set default price
          if (transactionMeta?._rm?.webEntry?.trans_inv?.defaultPrice) {
            return (
              prodDetails?.[
                transactionMeta?._rm?.webEntry?.trans_inv?.defaultPrice ??
                  'cPrice1'
              ] ??
              prodDetails?.cPrice1 ??
              0
            );
          }

          // purchase order and purchases
          if ([PURCHASE_ORDER, PURCHASES, WAVG_PURCHASES].includes(invType))
            return prodDetails?.cCost ?? 0;
          else return prodDetails?.cPrice1 ?? 0;
        }

        const prop =
          ALL_PRICE_TYPES.find(item => item.value === +priceType)?.prop ||
          'cPrice1';
        return prodDetails?.[prop] ?? 0;
      };

      if (
        hasDuplicateSN(inventoryProducts, rest?.SN ?? '', id) ||
        hasDuplicateSN2(inventoryProducts, rest?.SN2 ?? '', id) ||
        (hasDuplicateProduct(ixProd) && !allow_adding_duplicate_products)
      ) {
        message.info('Item already added.');
        return;
      }

      if (hasDuplicateProduct(ixProd)) {
        const ok = await confirmPrompt(
          `${frontEnd?.labels?.products?.sProd ??
            'Product'} already added. Would you like to add it again?`
        );

        if (!ok) return; // user canceled
        usingIndex.shift();
      }

      let prodPrice = 0;
      // Find the product info in uniqueData
      const prodInfo = uniqueData.find(item => item.ixProd === prodId);
      if (prodInfo?.category === 'main') {
        // Assign to the outer variable, do not redeclare
        prodPrice = getPrice(prodDetails);
      }

      const qtyResults = qtyPCToCS({
        libQtyCS: prod?.qtyCS ?? 0,
        libQtySC: prod?.qtySC ?? 0,
        qty: product?.invQty ?? 0,
        keyProp: 'inv'
      });

      let productVatCode = 1,
        detail_items = [],
        qtyREC = 0,
        unit = 0,
        qty = 1,
        qtyCS = 1,
        qtyPC = 1,
        priceCS = 0;

      // get product details
      const { data: prod, success } = await request.get(
        `/setup/prod/${prodDetails.ixProd}`
      );

      if (!success) message.error('Failed to get item info.');

      if (success && vatAllow && (INvisible || OUTvisible))
        productVatCode = vatType === 1 ? prod?.defVATCode ?? 1 : 0;

      if (
        product?.invQty > 0 &&
        qtyInputUIMode === QTY_INPUT_UI_MODE.WITH_UNIT
      ) {
        qty = product?.invQty;
        qtyCS = prod?.qtyCS ?? 0;
        qtyPC = prod?.qtySC ?? 0;
        // priceCS = toDecimal4(prodPrice * product?.invQty);
        // unit = 0;
        // unit =
        //   (prod.qtyCS ?? 0) === 0 && (prod.qtySC ?? 0) === 0
        //     ? 0 // both empty
        //     : (prod.qtyCS ?? 0) === 0 && (prod.qtySC ?? 0) > 0
        //     ? 1 // only SC has value
        //     : (prod.qtyCS ?? 0) > 0 && (prod.qtySC ?? 0) === 0
        //     ? 2 // only CS has value
        //     : (prod.qtyCS ?? 0) > 0 && (prod.qtySC ?? 0) > 0
        //     ? 2
        //     : 0; // both have value
      }

      discountsAndVAT.set({
        priceGross: prodPrice,
        vatCode: productVatCode,
        qty
      });

      const {
        vatIN,
        vatINAmt,
        vatOUT,
        vatOUTAmt,
        netPrice,
        taxablePrice,
        taxableAmt,
        muRate,
        muAmt,
        muSubTotal,
        discOutrightAmt,
        discOutrightSubTotal,
        discRate,
        discAmt,
        discSubTotal,
        discValue,
        discValueSubTotal
      } = discountsAndVAT.calculate();

      setInventoryProducts(prev => [
        ...prev.map((data, i) => {
          if (i === index) {
            return {
              ...data,
              vatRate: taxVATRate,
              vatCode: productVatCode,
              product: {
                ...data.product,
                error: false,
                errorMessage: '',
                value: ixProd,
                ...rest
              },
              price: {
                ...data.price,
                value: prodPrice
              },

              priceCS,
              //TODO: NEED TO FIX THIS
              // priceSC, uncomment this needs to fix this

              base_qtyCS: rest.qtyCS,
              base_qtySC: rest.qtySC,

              qtyCS,
              qtyPC,

              qty: {
                ...data.qty,
                value: qty
              },

              unit: rest.unit,
              unitCS: rest.unitCS,
              unitSC: rest.unitSC,
              ixUnit: unit,

              qtyREC,
              vatIN,
              vatINAmt,
              vatOUT,
              vatOUTAmt,
              priceGross: prodPrice,
              finalPrice: netPrice,
              taxablePrice,
              taxableAmt,
              muRate,
              muAmt,
              muSubTotal,
              discOutrightAmt,
              discOutrightSubTotal,
              discRate,
              discAmt,
              discSubTotal,
              discValue,
              discValueTotal: {
                ...data.discValueTotal,
                value: discValue
              },
              discValueSubTotal,
              reqSN: rest?.reqSN,
              reqSN2: rest?.reqSN2,
              detail_items
            };
          }

          return data;
        })
      ]);

      // TODO: NEED TO FIX
      // if (shouldChooseSerialOrBatchNo && (rest?.reqSN || rest?.reqSN2)) {
      //   openSelectSN();

      //   openSelectBN();
      //   setCloseProductCallback(() => () => {
      //     focusProductField('ArrowDown', itemNo + 1);
      //   });
      // }

      // if (
      //   (shouldChooseSerialOrBatchNo ||
      //     invType === STOCK_ADJUSTMENT_BY_VARIANCE) &&
      //   (inv?.BN?.required_categories ?? []).includes(rest?.ixCat)
      // ) {
      //   openSelectBN();
      //   setCloseProductCallback(() => () => {
      //     focusProductField('ArrowDown', itemNo + 1);
      //   });
      // }

      // focusProductField('ArrowDown', itemNo + 1);
    }
  } catch (err) {
    notif.error('Maybe on your deals dint match on branch');
    console.error(err);
  } finally {
    setIsSaving(false); // stop loading after loop is done
  }
}
